import { supabase } from '@/lib/supabase';
import { debugLog } from '@/utils/logger'
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { sha256Hex } from '@/lib/sha256';

// Client IDs Google (pas des secrets — déjà en clair dans Info.plist et
// capacitor.config.ts). Le plugin @capgo/capacitor-social-login ne lit PAS le
// bloc SocialLogin de capacitor.config.ts : sans initialize() runtime, login()
// échoue toujours et on retombait sur le fallback Safari (tâche 17579292).
// - iOS : doit matcher GIDClientID dans ios/App/App/Info.plist (scheme inversé
//   déclaré dans CFBundleURLSchemes).
// - Web client ID : utilisé pour Android et web.
// Projet GCP `harmonia-500312` (recréé le 2026-06-23 après corruption de
// l'ancien projet OAuth 80499433118 — cf. docs/google-oauth-recovery-plan.md).
const GOOGLE_IOS_CLIENT_ID = '372680791715-k6ukhcad20aos2qjeiusl0jthtochas8.apps.googleusercontent.com';
const GOOGLE_WEB_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
    || '372680791715-2htlt7rv3qb9ttbgio7rd7db5hjg8clj.apps.googleusercontent.com';

let socialLoginInitPromise: Promise<void> | null = null;

function ensureSocialLoginInitialized(): Promise<void> {
    if (!socialLoginInitPromise) {
        socialLoginInitPromise = SocialLogin.initialize({
            google: {
                iOSClientId: GOOGLE_IOS_CLIENT_ID,
                webClientId: GOOGLE_WEB_CLIENT_ID,
            },
        }).catch((error) => {
            // Permet de retenter au prochain login au lieu de rester bloqué
            // sur une init échouée.
            socialLoginInitPromise = null;
            throw error;
        });
    }
    return socialLoginInitPromise;
}

// Extend Window interface for Google Sign-In
declare global {
    interface Window {
        handleGoogleSignIn?: (response: { credential: string }) => Promise<void>;
        google?: {
            accounts: {
                id: {
                    initialize: (config: any) => void;
                    prompt: () => void;
                    renderButton: (element: HTMLElement, config: any) => void;
                };
            };
        };
    }
}

export const googleAuthService = {
    /**
     * Sign in with Google OAuth
     */
    async signInWithGoogle(): Promise<void> {
        try {
            const isNative = Capacitor.isNativePlatform();
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

            let redirectUrl: string;

            if (isNative) {
                redirectUrl = 'com.withharmonia.app://auth/callback';
            } else if (isLocalhost) {
                // For local development, use the localhost URL
                redirectUrl = `${window.location.origin}/auth/callback`;
                // console.log('🔐 [Google Auth] Local development detected, using localhost redirect');
            } else {
                // For production web, use the Vercel URL
                redirectUrl = 'https://app.withharmonia.com/auth/callback';
                // console.log('🔐 [Google Auth] Production web detected, using Vercel redirect');
            }

            debugLog('🔐 [Google Auth] Platform:', isNative ? 'native' : 'web');
            debugLog('🔐 [Google Auth] Environment:', isLocalhost ? 'local' : 'production');
            debugLog('🔐 [Google Auth] Redirect URL:', redirectUrl);

            if (isNative) {
                await this.signInWithGoogleNative(redirectUrl);
            } else {
                await this.signInWithGoogleWeb(redirectUrl);
            }
        } catch (error) {
            console.error('❌ [Google Auth] Sign in error:', error);
            throw error;
        }
    },

    /**
     * Sign in with Google using native plugin (iOS/Android) or Browser fallback
     */
    async signInWithGoogleNative(redirectUrl: string): Promise<void> {
        try {
            // Try the native plugin first (100% in-app, similar to Apple Sign-In)
            debugLog('🔐 [Google Auth] Attempting native plugin method (100% in-app)...');
            await this.signInWithGoogleNativePlugin();
            debugLog('✅ [Google Auth] Native plugin authentication successful');
            return; // Success - no need for fallback
        } catch (nativeError: any) {
            console.warn('⚠️ [Google Auth] Native plugin failed, falling back to Browser OAuth:', nativeError.message);

            // Fallback to Browser method if native plugin fails
            // This is compatible with Supabase Web client ID
            debugLog('🔐 [Google Auth] Using Browser OAuth method (fallback)');
            try {
                await this.signInWithGoogleNativeBrowser(redirectUrl);
            } catch (browserError) {
                console.error('❌ [Google Auth] Both native plugin and Browser OAuth failed');
                throw browserError;
            }
        }
    },

    /**
     * Sign in with Google using native SocialLogin plugin (iOS/Android)
     * Similar to Apple Sign-In - stays 100% in-app
     */
    async signInWithGoogleNativePlugin(): Promise<void> {
        try {
            debugLog('🔐 [Google Auth Native] Starting native Google Sign In');
            debugLog('🔐 [Google Auth Native] SocialLogin object:', typeof SocialLogin);
            debugLog('🔐 [Google Auth Native] SocialLogin.login:', typeof SocialLogin?.login);

            // Check if plugin is properly imported
            if (!SocialLogin || typeof SocialLogin.login !== 'function') {
                throw new Error('SocialLogin plugin is not properly initialized. Make sure @capgo/capacitor-social-login is installed and synced.');
            }

            // initialize() est requis avant login() (une seule fois par session)
            debugLog('🔐 [Google Auth Native] Ensuring SocialLogin is initialized...');
            await ensureSocialLoginInitialized();

            // Sign in with Google using native plugin
            debugLog('🔐 [Google Auth Native] Calling SocialLogin.login...');
            const result = await SocialLogin.login({
                provider: 'google',
                options: {
                    scopes: ['profile', 'email'],
                    // Sans ça, le SDK iOS restaure silencieusement le dernier
                    // compte connecté (restorePreviousSignIn) sans afficher le
                    // sélecteur de compte.
                    forcePrompt: true,
                },
            });

            debugLog('🔐 [Google Auth Native] SocialLogin response:', JSON.stringify(result));

            // Extract the actual result from the response structure
            // The plugin returns: { provider: 'google', result: { idToken: '...', accessToken: {...} } }
            // Type guard: check if result has a 'result' property (wrapped response)
            const googleResult = ('result' in result && result.result) ? result.result : result;
            
            // Get idToken (preferred) or accessToken.token as fallback
            // Use type assertion since the plugin response structure isn't fully typed
            type GoogleAuthResult = { idToken?: string; accessToken?: { token?: string } };
            const authResult = googleResult as GoogleAuthResult;
            const idToken = authResult.idToken || authResult.accessToken?.token;
            
            if (!idToken) {
                console.error('❌ [Google Auth Native] Response structure:', JSON.stringify(result));
                throw new Error('No ID token or access token received from Google');
            }

            debugLog('🔐 [Google Auth Native] ID token extracted successfully');
            debugLog('🔐 [Google Auth Native] ID token preview:', idToken.substring(0, 50) + '...');

            // Wait for network to stabilize after modal dismissal
            // Shorter delay on physical devices, longer on simulator
            const isSimulator = await this.isSimulator();
            const waitTime = isSimulator ? 5000 : 1000; // 5s for simulator, 1s for physical device
            debugLog(`⏳ [Google Auth Native] Waiting for network to stabilize (${waitTime/1000} seconds)...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));

            // Verify Supabase connection before attempting sign in
            // On iOS Simulator, network can be lost after closing native modals
            debugLog('🔐 [Google Auth Native] Verifying Supabase connection...');
            let networkVerified = false;
            for (let i = 0; i < 3; i++) {
                try {
                    const { error: sessionError } = await supabase.auth.getSession();
                    if (!sessionError) {
                        networkVerified = true;
                        debugLog('✅ [Google Auth Native] Supabase connection verified');
                        break;
                    } else {
                        console.warn(`⚠️ [Google Auth Native] Session check warning (attempt ${i + 1}/3):`, sessionError);
                    }
                } catch (connError: any) {
                    console.warn(`⚠️ [Google Auth Native] Connection check failed (attempt ${i + 1}/3):`, connError);
                    if (i < 2) {
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }
            }
            
            if (!networkVerified) {
                console.warn('⚠️ [Google Auth Native] Network verification failed, but continuing anyway...');
                console.warn('⚠️ [Google Auth Native] This is a known issue on iOS Simulator. Testing on a physical device is recommended.');
            }

            // Fewer retries on physical devices (network is more reliable)
            // Reuse isSimulator from above (line 158)
            let retries = isSimulator ? 5 : 2; // 5 retries for simulator, 2 for physical device
            debugLog(`🔐 [Google Auth Native] Using ${retries} retry attempts (${isSimulator ? 'simulator' : 'physical device'})`);
            let lastError: any;
            let authData: any;

            while (retries > 0) {
                try {
                    const attemptNumber = (isSimulator ? 6 : 3) - retries;
                    const maxAttempts = isSimulator ? 5 : 2;
                    debugLog(`🔐 [Google Auth Native] Attempting Supabase sign in (attempt ${attemptNumber}/${maxAttempts})...`);
                    debugLog('🔐 [Google Auth Native] Supabase URL:', import.meta.env.VITE_SUPABASE_URL);

                    // Use signInWithIdToken with the Google ID token (similar to Apple Sign-In)
                    const supabaseResult = await supabase.auth.signInWithIdToken({
                        provider: 'google',
                        token: idToken,
                    });

                    if (supabaseResult.error) {
                        lastError = supabaseResult.error;
                        const attemptNumber = (isSimulator ? 6 : 3) - retries;
                        const maxAttempts = isSimulator ? 5 : 2;
                        console.error(`❌ [Google Auth Native] Supabase sign in error (attempt ${attemptNumber}/${maxAttempts}):`, supabaseResult.error);
                        console.error(`❌ [Google Auth Native] Error name:`, supabaseResult.error.name);
                        console.error(`❌ [Google Auth Native] Error status:`, supabaseResult.error.status);
                        console.error(`❌ [Google Auth Native] Error message:`, supabaseResult.error.message);

                        // Retry on network errors (including status 0)
                        const isNetworkError = 
                            supabaseResult.error.name?.includes('Fetch') || 
                            supabaseResult.error.name?.includes('Network') ||
                            supabaseResult.error.status === 0 ||
                            supabaseResult.error.message?.includes('network') ||
                            supabaseResult.error.message?.includes('Load failed');

                        if (isNetworkError) {
                            retries--;
                            if (retries > 0) {
                                const waitTime = 3000; // Increased wait time
                                debugLog(`🔄 [Google Auth Native] Network error detected, retrying in ${waitTime/1000} seconds... (${retries} attempts left)`);
                                await new Promise(resolve => setTimeout(resolve, waitTime));
                                continue;
                            } else {
                                // No more retries left, break out of loop
                                // The error will be thrown after the loop
                                break;
                            }
                        }
                        throw supabaseResult.error;
                    }

                    authData = supabaseResult.data;
                    debugLog('✅ [Google Auth Native] Successfully signed in to Supabase');
                    debugLog('🔐 [Google Auth Native] User ID:', authData.user?.id);
                    debugLog('🔐 [Google Auth Native] User email:', authData.user?.email);
                    break;
                } catch (err: any) {
                    lastError = err;
                    const isNetworkError = 
                        err.name?.includes('Fetch') || 
                        err.name?.includes('Network') ||
                        err.status === 0 ||
                        err.message?.includes('network') ||
                        err.message?.includes('Load failed');

                    if (isNetworkError) {
                        retries--;
                        if (retries > 0) {
                            const waitTime = 3000; // Increased wait time
                            debugLog(`🔄 [Google Auth Native] Network error caught, retrying in ${waitTime/1000} seconds... (${retries} attempts left)`);
                            await new Promise(resolve => setTimeout(resolve, waitTime));
                        } else {
                            // No more retries left, break out of loop
                            // The error will be thrown after the loop
                            break;
                        }
                    } else {
                        // Non-network error, don't retry
                        console.error('❌ [Google Auth Native] Non-network error, not retrying:', err);
                        throw err;
                    }
                }
            }

            // Verify that authentication actually succeeded
            if (!authData || !authData.user) {
                // If we exhausted all retries, throw the last error
                if (lastError) {
                    console.error('❌ [Google Auth Native] All retry attempts failed');
                    console.error('❌ [Google Auth Native] Last error:', lastError);
                    const maxAttempts = isSimulator ? 5 : 2;
                    const errorMessage = `Failed to authenticate with Supabase after ${maxAttempts} attempts. ` +
                        (isSimulator 
                            ? `This is often a network issue on iOS Simulator. ` 
                            : ``) +
                        `Error: ${lastError.message || lastError.name || 'Unknown error'}. ` +
                        (isSimulator 
                            ? `Please try on a physical iOS device for better network reliability.` 
                            : `Please check your network connection and try again.`);
                    console.error('❌ [Google Auth Native] Throwing error:', errorMessage);
                    throw new Error(errorMessage);
                } else {
                    const errorMessage = 'Failed to authenticate with Supabase: No authentication data received';
                    console.error('❌ [Google Auth Native] Throwing error:', errorMessage);
                    throw new Error(errorMessage);
                }
            }
            
            // Success - log and return
            debugLog('✅ [Google Auth Native] Authentication successful, user authenticated');

        } catch (error: any) {
            console.error('❌ [Google Auth Native] Error:', error);

            // Handle user cancellation gracefully
            if (error.message?.includes('User cancelled') || error.code === '1001') {
                debugLog('ℹ️ [Google Auth Native] User cancelled sign in');
                throw new Error('User cancelled sign in');
            }

            throw error;
        }
    },

    /**
     * Sign in with Google using Capacitor Browser (iOS/Android) - Fallback method
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async signInWithGoogleNativeBrowser(redirectUrl: string): Promise<void> {
        try {
            // Force the redirect URL to use our custom scheme without localhost
            const customRedirectUrl = 'com.withharmonia.app://auth/callback';

            debugLog('🔐 [Google Auth Browser] Using custom redirect URL:', customRedirectUrl);

            // Start OAuth flow with Supabase
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: customRedirectUrl,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent'
                    }
                }
            });

            if (error) throw error;

            // Log the generated OAuth URL for debugging
            if (data.url) {
                debugLog('🔐 [Google Auth] Generated OAuth URL:', data.url);

                // Check if the redirect_to parameter is correct
                const redirectParam = data.url.includes('redirect_to=')
                    ? decodeURIComponent(data.url.split('redirect_to=')[1].split('&')[0])
                    : 'Not found';
                debugLog('🔐 [Google Auth] Redirect parameter:', redirectParam);

                // Fix the URL by removing localhost from the redirect_to parameter
                let fixedUrl = data.url;
                // console.log('🔐 [Google Auth] Original URL to fix:', fixedUrl);

                if (redirectParam.includes('localhost')) {
                    // console.log('🔐 [Google Auth] localhost detected, fixing URL...');
                    const cleanRedirect = 'com.withharmonia.app://auth/callback';
                    const encodedCleanRedirect = encodeURIComponent(cleanRedirect);
                    // console.log('🔐 [Google Auth] Clean redirect:', cleanRedirect);
                    // console.log('🔐 [Google Auth] Encoded clean redirect:', encodedCleanRedirect);

                    const originalEncoded = encodeURIComponent(redirectParam);
                    // console.log('🔐 [Google Auth] Original encoded redirect:', originalEncoded);

                    fixedUrl = data.url.replace(
                        `redirect_to=${originalEncoded}`,
                        `redirect_to=${encodedCleanRedirect}`
                    );
                    // console.log('🔐 [Google Auth] Fixed OAuth URL:', fixedUrl);

                    // Verify the replacement worked
                    // console.log('🔐 [Google Auth] New redirect parameter:', newRedirectParam);
                } else {
                    // console.log('🔐 [Google Auth] No localhost detected, URL is clean');
                }

                // Open the fixed OAuth URL in Capacitor Browser
                // console.log('🔐 [Google Auth] Opening OAuth URL in native browser');

                // Note: OAuth callback is handled by App.vue's appUrlOpen listener
                // We don't set up a duplicate listener here to avoid conflicts
                // The browser will be closed by App.vue when the callback is received
                
                // Set up a resume listener to close browser if user returns without callback
                const appResumeListenerPromise = App.addListener('appStateChange', async (state) => {
                    if (state.isActive) {
                        // User returned to app, check if we have a session
                        const { data: { session } } = await supabase.auth.getSession();
                        if (session) {
                            try {
                                await Browser.close();
                            } catch (e) {
                                // Browser might already be closed
                            }
                        }
                    }
                });

                // Clean up listener after 5 minutes
                setTimeout(async () => {
                    try {
                        const appResumeListener = await appResumeListenerPromise;
                        appResumeListener.remove();
                    } catch (error) {
                        console.warn('⚠️ [Google Auth] Error cleaning up listener:', error);
                    }
                }, 300000);

                // OLD CODE - REMOVED TO AVOID DUPLICATE CALLBACK HANDLING
                // The callback is now handled by App.vue
                /*
                const appUrlListenerPromise = App.addListener('appUrlOpen', async (event) => {
                    debugLog('🔐 [Google Auth] App URL opened:', event.url);
                    debugLog('🔐 [Google Auth] Full event:', JSON.stringify(event));

                    if (event.url.includes('auth/callback')) {
                        debugLog('🔐 [Google Auth] OAuth callback detected, closing browser...');

                        try {
                            // Close the browser
                            await Browser.close();
                            debugLog('🔐 [Google Auth] Browser closed successfully');

                            // Extract the OAuth code from the callback URL
                            const url = new URL(event.url);
                            const code = url.searchParams.get('code');
                            const state = url.searchParams.get('state');

                            debugLog('🔐 [Google Auth] Extracted OAuth code:', code);
                            debugLog('🔐 [Google Auth] Extracted OAuth state:', state);

                            if (code) {
                                // Add a small delay to allow network to stabilize after browser close
                                // This fixes an iOS simulator bug where network is lost briefly
                                debugLog('⏳ [Google Auth] Waiting for network to stabilize...');
                                await new Promise(resolve => setTimeout(resolve, 1000));

                                // Exchange the code for a session with retry logic
                                debugLog('🔐 [Google Auth] Exchanging OAuth code for session...');

                                let retries = 3;
                                let lastError;

                                while (retries > 0) {
                                    try {
                                        const { data, error } = await supabase.auth.exchangeCodeForSession(code);

                                        if (error) {
                                            console.error(`❌ [Google Auth] Code exchange error (attempt ${4 - retries}/3):`, error);
                                            lastError = error;

                                            // If it's a network error, retry
                                            if (error.message?.includes('Load failed') || error.message?.includes('network')) {
                                                retries--;
                                                if (retries > 0) {
                                                    debugLog(`🔄 [Google Auth] Retrying in 2 seconds... (${retries} attempts left)`);
                                                    await new Promise(resolve => setTimeout(resolve, 2000));
                                                    continue;
                                                }
                                            }
                                            throw error;
                                        }

                                        if (data.session) {
                                            debugLog('✅ [Google Auth] OAuth code exchanged successfully!');
                                            debugLog('🔐 [Google Auth] User ID:', data.session.user.id);
                                            debugLog('🔐 [Google Auth] User email:', data.session.user.email);
                                            break; // Success, exit retry loop
                                        }
                                    } catch (err) {
                                        lastError = err;
                                        retries--;
                                        if (retries > 0) {
                                            debugLog(`🔄 [Google Auth] Retrying in 2 seconds... (${retries} attempts left)`);
                                            await new Promise(resolve => setTimeout(resolve, 2000));
                                        }
                                    }
                                }

                                // If we exhausted all retries, throw the last error
                                if (retries === 0 && lastError) {
                                    console.error('❌ [Google Auth] All retry attempts failed');
                                    throw lastError;
                                }

                                const { data } = await supabase.auth.getSession();
                                if (data.session) {
                                    debugLog('✅ [Google Auth] OAuth code exchanged successfully!');
                                    debugLog('🔐 [Google Auth] User ID:', data.session.user.id);
                                    debugLog('🔐 [Google Auth] User email:', data.session.user.email);

                                    // Force an auth state change to trigger the store update
                                    // console.log('🔐 [Google Auth] Triggering auth state change...');
                                    await supabase.auth.onAuthStateChange((event, session) => {
                                        // console.log('🔐 [Google Auth] Auth state change event:', event);
                                        if (session) {
                                            // console.log('✅ [Google Auth] Auth state change successful with session');
                                        }
                                    });
                                } else {
                                    console.warn('⚠️ [Google Auth] No session after code exchange');
                                }
                            } else {
                                console.error('❌ [Google Auth] No OAuth code found in callback URL');
                            }

                            // Handle the OAuth callback
                            await this.handleOAuthCallback(event.url);
                        } catch (error) {
                            console.error('❌ [Google Auth] Error handling callback:', error);
                        }
                    } else if (event.url.includes('onboarding') || event.url.includes('vercel.app')) {
                        // console.log('🔐 [Google Auth] Web callback detected, this means OAuth succeeded but Supabase redirected to web');
                        // console.log('🔐 [Google Auth] Attempting to extract session and return to app...');

                        try {
                            // Close the browser
                            await Browser.close();
                            // console.log('🔐 [Google Auth] Browser closed successfully');

                            // Check if we have a session (OAuth might have succeeded)
                            const { data, error } = await supabase.auth.getSession();
                            if (error) {
                                console.error('❌ [Google Auth] Session check error:', error);
                                return;
                            }

                            if (data.session) {
                                // console.log('✅ [Google Auth] Session found after web redirect, OAuth successful');
                                // The auth state change will be handled by the auth store
                            } else {
                                console.warn('⚠️ [Google Auth] No session found after web redirect');
                            }
                        } catch (error) {
                            console.error('❌ [Google Auth] Error handling web callback:', error);
                        }
                    } else {
                        // console.log('🔐 [Google Auth] URL opened but not OAuth callback:', event.url);
                    }
                });
                */

                // Open the fixed OAuth URL
                // Use fullscreen presentation style to match capacitor.config.ts
                // This ensures Safari View Controller opens correctly on iOS
                debugLog('🔐 [Google Auth] Attempting to open OAuth URL in browser...');
                debugLog('🔐 [Google Auth] URL length:', fixedUrl.length);
                debugLog('🔐 [Google Auth] URL preview:', fixedUrl.substring(0, 100) + '...');
                
                try {
                    // Check if Browser plugin is available
                    if (!Browser || typeof Browser.open !== 'function') {
                        throw new Error('Browser plugin is not available');
                    }

                    // Try opening with fullscreen first (required for iOS)
                    await Browser.open({
                        url: fixedUrl,
                        presentationStyle: 'fullscreen',
                        toolbarColor: '#ffffff'
                    });
                    debugLog('✅ [Google Auth] Browser opened successfully with fullscreen');
                    
                    // Warning: On iOS Simulator, Safari external browser may open instead of Safari View Controller
                    // This is a known limitation of iOS Simulator. On physical devices, Safari View Controller works correctly.
                    console.warn('⚠️ [Google Auth] Note: On iOS Simulator, Safari external browser may open instead of Safari View Controller.');
                    console.warn('⚠️ [Google Auth] This prevents OAuth callbacks from working. Please test on a physical iOS device.');
                } catch (browserError: any) {
                    console.error('❌ [Google Auth] Failed to open browser:', browserError);
                    console.error('❌ [Google Auth] Error details:', JSON.stringify(browserError));
                    
                    // Fallback 1: Try without presentationStyle
                    try {
                        debugLog('🔄 [Google Auth] Trying fallback method 1: without presentationStyle');
                        await Browser.open({
                            url: fixedUrl
                        });
                        debugLog('✅ [Google Auth] Browser opened with fallback method 1');
                    } catch (fallback1Error: any) {
                        console.error('❌ [Google Auth] Fallback 1 failed:', fallback1Error);
                        
                        // Fallback 2: Try with popover (sometimes works better on simulator)
                        try {
                            debugLog('🔄 [Google Auth] Trying fallback method 2: with popover');
                            await Browser.open({
                                url: fixedUrl,
                                presentationStyle: 'popover'
                            });
                            debugLog('✅ [Google Auth] Browser opened with fallback method 2');
                        } catch (fallback2Error: any) {
                            console.error('❌ [Google Auth] All browser open methods failed');
                            console.error('❌ [Google Auth] Last error:', JSON.stringify(fallback2Error));
                            
                            // Provide helpful error message
                            const errorMessage = fallback2Error?.message || 'Unknown error';
                            throw new Error(
                                `Failed to open OAuth browser on iOS Simulator. ` +
                                `This is a known issue with iOS Simulator. ` +
                                `Error: ${errorMessage}. ` +
                                `Please try on a physical iOS device or use web mode for testing.`
                            );
                        }
                    }
                }

                // Note: OAuth callback handling is done by App.vue
                // We only keep a resume listener to close browser if user returns without callback
                // This listener is already set up above (line 333) // 5 minutes timeout
            }
        } catch (error) {
            console.error('❌ [Google Auth] Native sign in error:', error);
            throw error;
        }
    },

    /**
     * Sign in with Google using One Tap (web only)
     * This provides a seamless sign-in experience without redirects
     */
    async signInWithGoogleWeb(redirectUrl: string): Promise<void> {
        try {
            debugLog('🔐 [Google Auth Web] Attempting Google One Tap sign-in...');
            debugLog('💡 [Google Auth Web] One Tap provides seamless in-app authentication without page redirect');

            // Try Google One Tap first (no redirect, stays in app)
            await this.signInWithGoogleOneTap();
            debugLog('✅ [Google Auth Web] Google One Tap successful - no page redirect needed');
        } catch (oneTapError: any) {
            console.warn('⚠️ [Google Auth Web] Google One Tap failed, falling back to OAuth redirect:', oneTapError.message);
            console.warn('⚠️ [Google Auth Web] OAuth redirect will cause a full page redirect. This is expected if One Tap is not available.');

            // Fallback to traditional OAuth redirect (will redirect the page)
            // Note: This is a limitation of Supabase OAuth - it always redirects the full page
            await this.signInWithGoogleOAuthRedirect(redirectUrl);
        }
    },

    /**
     * Sign in with Google One Tap (no redirect)
     */
    async signInWithGoogleOneTap(): Promise<void> {
        // Préparation asynchrone (lib + nonce) SORTIE du Promise : un executor
        // async avalerait ces erreurs. Ici les `await` rejettent proprement.
        await this.loadGoogleSignInLibrary();

        if (!window.google) {
            throw new Error('Google Sign-In library failed to load');
        }
        const google = window.google;

        // Get Google Client ID from environment
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        if (!clientId) {
            throw new Error('Google Client ID not configured. Set VITE_GOOGLE_CLIENT_ID in your .env file');
        }

        // Generate nonce for security
        const [nonce, hashedNonce] = await this.generateNonce();

        return new Promise<void>((resolve, reject) => {
            try {
                // Initialize Google One Tap
                google.accounts.id.initialize({
                    client_id: clientId,
                    callback: async (response: { credential: string }) => {
                        try {
                            debugLog('🔐 [Google Auth Web] One Tap callback received');

                            // Sign in with Supabase using the ID token
                            const { data, error } = await supabase.auth.signInWithIdToken({
                                provider: 'google',
                                token: response.credential,
                                nonce,
                            });

                            if (error) {
                                console.error('❌ [Google Auth Web] Supabase sign-in error:', error);
                                reject(error);
                                return;
                            }

                            debugLog('✅ [Google Auth Web] Sign-in successful');
                            debugLog('🔐 [Google Auth Web] User ID:', data.user?.id);
                            debugLog('🔐 [Google Auth Web] User email:', data.user?.email);
                            
                            // Stocker temporairement le provider OAuth pour le router guard
                            // Note: signInWithIdToken peut retourner "email" dans app_metadata.provider,
                            // mais nous savons que c'est une connexion Google car nous appelons avec provider: 'google'
                            localStorage.setItem('harmonia_oauth_provider', 'google');
                            debugLog('💾 [Google Auth Web] Stored OAuth provider: google');
                            
                            // Attendre que getUser() soit disponible (important pour éviter les erreurs "User not authenticated")
                            debugLog('⏳ [Google Auth Web] Verifying user session is fully propagated...');
                            let getUserRetries = 10;
                            let userAvailable = false;
                            
                            while (getUserRetries > 0 && !userAvailable) {
                                const { data: { user }, error: getUserError } = await supabase.auth.getUser();
                                
                                if (getUserError) {
                                    console.warn(`⚠️ [Google Auth Web] getUser error (${11 - getUserRetries}/10):`, getUserError);
                                }
                                
                                if (user) {
                                    debugLog('✅ [Google Auth Web] User session fully propagated');
                                    userAvailable = true;
                                    break;
                                }
                                
                                getUserRetries--;
                                if (getUserRetries > 0) {
                                    // Attendre un peu avant de réessayer
                                    await new Promise(resolve => setTimeout(resolve, 200));
                                }
                            }
                            
                            if (!userAvailable) {
                                console.error('❌ [Google Auth Web] User session not available after retries');
                                reject(new Error('User session not available'));
                                return;
                            }
                            
                            resolve();
                        } catch (err) {
                            console.error('❌ [Google Auth Web] Callback error:', err);
                            reject(err);
                        }
                    },
                    nonce: hashedNonce,
                    use_fedcm_for_prompt: true, // Use Federated Credential Management API
                });

                // Trigger the One Tap / FedCM prompt.
                // NOTE: isNotDisplayed(), isDisplayed(), getNotDisplayedReason() are deprecated
                // as part of the FedCM migration and must not be used (they return stale/invalid
                // data and calling them causes "s.join is not a function" errors).
                // Auth is handled entirely by the credential callback above.
                google.accounts.id.prompt();

                // Set a longer timeout to give user more time
                // One Tap might take a moment to appear
                setTimeout(() => {
                    reject(new Error('Google One Tap prompt was closed or timed out'));
                }, 60000); // 60 seconds timeout (increased from 30)

            } catch (error) {
                console.error('❌ [Google Auth Web] One Tap initialization error:', error);
                reject(error);
            }
        });
    },

    /**
     * Sign in with Google using standard OAuth redirect (fallback)
     * Note: This will redirect the full page. One Tap should be preferred.
     */
    async signInWithGoogleOAuthRedirect(redirectUrl: string): Promise<void> {
        debugLog('🔐 [Google Auth Web] Starting OAuth redirect with redirect:', redirectUrl);
        console.warn('⚠️ [Google Auth Web] Using OAuth redirect (full page redirect). One Tap is preferred for better UX.');

        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: redirectUrl,
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent'
                }
            }
        });

        if (error) {
            console.error('❌ [Google Auth Web] OAuth error:', error);
            throw error;
        }

        if (data.url) {
            debugLog('🔐 [Google Auth Web] OAuth URL generated, redirecting...');
            window.location.href = data.url;
        } else {
            throw new Error('No OAuth URL generated');
        }
    },

    /**
     * Generate nonce for Google One Tap security
     * Returns [plainNonce, hashedNonce]
     */
    async generateNonce(): Promise<[string, string]> {
        const randomBytes = new Uint8Array(32);
        crypto.getRandomValues(randomBytes);
        const nonce = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('');

        // Hash the nonce using SHA-256 (resilient to missing crypto.subtle on
        // native WebViews — see @/lib/sha256).
        const hashedNonce = await sha256Hex(nonce);

        return [nonce, hashedNonce];
    },

    /**
     * Load Google Sign-In library
     */
    async loadGoogleSignInLibrary(): Promise<void> {
        // Check if already loaded
        if (window.google?.accounts?.id) {
            return;
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;

            script.onload = () => {
                debugLog('✅ [Google Auth Web] Google Sign-In library loaded');
                resolve();
            };

            script.onerror = () => {
                console.error('❌ [Google Auth Web] Failed to load Google Sign-In library');
                reject(new Error('Failed to load Google Sign-In library'));
            };

            document.head.appendChild(script);
        });
    },

    /**
     * Handle OAuth callback from native browser
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async handleOAuthCallback(url: string): Promise<void> {
        try {
            // console.log('🔐 [Google Auth] Handling OAuth callback');

            // Extract the session from the URL
            const { data, error } = await supabase.auth.getSession();

            if (error) {
                console.error('❌ [Google Auth] Session error:', error);
                throw error;
            }

            if (data.session) {
                // console.log('✅ [Google Auth] OAuth callback successful');
                // The auth state change will be handled by the auth store
            } else {
                console.warn('⚠️ [Google Auth] No session found after OAuth callback');
            }
        } catch (error) {
            console.error('❌ [Google Auth] OAuth callback handling error:', error);
            throw error;
        }
    },

    /**
     * Close the browser if it's open
     */
    async closeBrowser(): Promise<void> {
        try {
            await Browser.close();
        } catch (error) {
            console.warn('⚠️ [Google Auth] Browser close error:', error);
        }
    },

    /**
     * Check if running on iOS Simulator
     * Returns false by default (assumes physical device) for better UX
     */
    async isSimulator(): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) {
            return false;
        }
        
        try {
            const platform = Capacitor.getPlatform();
            if (platform === 'ios') {
                // On physical devices, network is more reliable
                // We default to false (physical device) to avoid unnecessary delays
                // The simulator check is mainly for logging/debugging
                return false;
            }
            return false;
        } catch (error) {
            console.warn('⚠️ [Google Auth] Error checking simulator status:', error);
            return false; // Default to false (physical device)
        }
    }
};
